import React, { useContext, useEffect, useState, useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import PropType from 'prop-types';
import { AppContext } from '../../util/AppContext';
import { getConfigValue } from '../../util/getConfigValue';
import { getRejectRescuerDetails } from  '../../API/rejectRescuer';
import { Table, Header, HeaderRow, HeaderCell, Body, Row, Cell } from '@table-library/react-table-library/table';
import { useTheme } from '@table-library/react-table-library/theme';
import { getTableColumnsSpec } from '../../util/getTableColumnsSpec.js';
import { getSanitizedHtml } from '../../util/getSanitizedHtml';

RejectClaimModal.propTypes = {
    show: PropType.bool,
    setShow: PropType.func,
    plan: PropType.shape({
        data: PropType.shape({
            name: PropType.string,
            planId: PropType.string,
        })
    })
};

function RejectClaimModal({ show, setShow, plan }) {
    const [appState] = useContext(AppContext);
    const [rejectRescuerData, setRejectRescuerData] = useState(null);
    const [tableData, setTableData] = useState({ nodes: [] });
    const [viewport, setViewport] = useState({ width: window.innerWidth });
    const { name, planId } = plan.data;

    useEffect(() => {
        function handleResize() {
            setViewport({ width: window.innerWidth });
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const columnsSpec = useMemo(() => {
        if (viewport.width < 990) {
            return '1fr';
        }
        if (rejectRescuerData?.headers) {
            return getTableColumnsSpec(rejectRescuerData.headers);
        }
        return '1fr';
    }, [rejectRescuerData, viewport.width]);

    const theme = useTheme({
        Table: `
            --data-table-library_grid-template-columns: ${columnsSpec};
            border-radius: 4px;
            overflow: hidden;
        `,
        HeaderRow: `
            background-color: var(--bs-dark);
            color: var(--bs-white);
            font-weight: bold;
        `,
        Row: `
            &:nth-of-type(odd) {
                background-color: var(--bs-gray-100);
            }
            &:hover {
                background-color: var(--bs-gray-100);
            }
            border-bottom: 1px solid var(--bs-border-color);
        `,
        BaseCell: `
            padding: 12px 8px;
            font-size: 14px;
            word-break: break-word;

            & > div {
                white-space: normal;
            }
        `
    });

    useEffect(() => {
        if (show) {
            const productId = appState?.currentProduct?.clientProductId;
            const prescriberId = appState?.prescriber?.displayPrescriberId;
            getRejectRescuerDetails(productId, planId, prescriberId)
                .then((results) => {
                    setRejectRescuerData(results);
                    if (results?.rows) {
                        setTableData({ nodes: results.rows });
                    }
                })  
        }
    }, [show, planId, appState, setTableData]);

    return (
        <Modal show={show} className='modal-lg' onHide={() => setShow(false)} scrollable={true}>
            <Modal.Header className='primary' closeButton>
                <Modal.Title className='modal-title reject-claim-modal-title'>{getConfigValue(appState.config?.RejectClaim?.Modal?.Title, appState)?.Value || 'Reject Codes / Recommended Action'}</Modal.Title>
            </Modal.Header>
            <Modal.Body className='modal-body'>
                {name && (
                    <p className='type-bold mb-3'>{name}</p>
                )}

                {rejectRescuerData?.headers && (
                    <Table
                        data={tableData}
                        theme={theme}
                        layout={{ custom: true, isDiv: true }}
                        className='table pd-table reject-claim-modal-table mb-0'
                    >
                        {(tableList) => (
                            <>
                                <Header>
                                    <HeaderRow className='table-dark d-none d-lg-contents'>
                                        {rejectRescuerData.headers.map((header) => (
                                            <HeaderCell key={header.key} scope='col' className={`px-2 py-2 ${header.title.includes('%') ? 'text-end' : ''}`}>
                                                {header.title}
                                            </HeaderCell>
                                        ))}
                                    </HeaderRow>
                                </Header>
                                <Body>
                                    {tableList.map((item) => (
                                        <Row key={item.data.code} item={item} className="row pd-plan-table-row align-items-center">
                                            {rejectRescuerData.headers.map((header) => (
                                                <Cell key={header.key} className={`px-2 py-2 ${header.title.includes('%') ? 'text-end' : ''}`}>
                                                    <span className={`d-block d-lg-none color-grey-d1 ${header.title.includes('%') ? 'text-end' : ''}`}>{item.columns[header.key] ? header.title : ""}</span>
                                                    <span className="form-check-label" dangerouslySetInnerHTML={getSanitizedHtml(item.columns[header.key])}></span>
                                                </Cell>
                                            ))}
                                        </Row>
                                    ))}
                                </Body>
                            </>
                        )}
                    </Table>
                )}

                <div className='mt-3 p-3 reject-claim-modal-disclaimer'>
                    <p>{getConfigValue(appState.config?.RejectClaim?.Modal?.FootNote?.Text, appState)?.Value}</p>
                    {getConfigValue(appState.config?.RejectClaim?.Modal?.Bottom?.Text, appState)?.Value}
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default RejectClaimModal;
